import { ChevronDownIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Switch } from '@/components/ui/catalyst/switch';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

import type { DisplayServiceWithWorkflows, DisplayWorkflow } from './types';
import { WorkflowToggleRow } from './WorkflowToggleRow';

interface ServiceAccordionProps {
  /** Service with workflow metadata (source of truth from API) */
  service: DisplayServiceWithWorkflows;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onServiceToggle: (active: boolean) => void;
  onWorkflowToggle: (workflowCode: string, enabled: boolean) => void;
  /** Async notes change handler for API persistence */
  onWorkflowNotesChange: (workflowCode: string, notes: string) => Promise<void>;
  disabled?: boolean;
  /** Whether this service is not configured for the matter */
  notConfigured?: boolean;
}

/**
 * Get border style class based on service state.
 */
const getServiceBorderClass = (
  isActive: boolean,
  notConfigured: boolean,
): string => {
  if (notConfigured) {
    return 'border-dashed border-zinc-300 dark:border-zinc-700 opacity-60';
  }
  if (isActive) {
    return 'border-zinc-200 dark:border-zinc-800';
  }
  return 'border-zinc-200 dark:border-zinc-800 opacity-60';
};

// -----------------------------------------------------------------------------
// ServiceContent: Renders workflow list or empty/not-configured states
// -----------------------------------------------------------------------------

interface ServiceContentProps {
  service: DisplayServiceWithWorkflows;
  notConfigured: boolean;
  disabled: boolean;
  onWorkflowToggle: (workflowCode: string, enabled: boolean) => void;
  onWorkflowNotesChange: (workflowCode: string, notes: string) => Promise<void>;
}

const ServiceContent: React.FC<ServiceContentProps> = ({
  service,
  notConfigured,
  disabled,
  onWorkflowToggle,
  onWorkflowNotesChange,
}) => {
  if (notConfigured) {
    return (
      <div className="px-3 py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
        This service is not configured for this matter.
        <br />
        <span className="text-xs">
          Services are configured when the matter is created.
        </span>
      </div>
    );
  }

  if (service.workflows.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
        No workflows configured
      </div>
    );
  }

  return (
    <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
      {service.workflows.map((workflow: DisplayWorkflow) => (
        <WorkflowToggleRow
          key={workflow.workflow_code}
          workflow={workflow}
          disabled={disabled}
          serviceDisabled={!service.is_active}
          onToggle={(enabled) =>
            onWorkflowToggle(workflow.workflow_code, enabled)
          }
          onNotesChange={(notes) =>
            onWorkflowNotesChange(workflow.workflow_code, notes)
          }
        />
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// ServiceAccordion: Main accordion component for a single service
// -----------------------------------------------------------------------------

/**
 * Accordion component for a single service.
 *
 * Architecture aligned with firm services:
 * - Uses service from props as source of truth
 * - Shows "Not configured" badge for services not in MatterService table
 */
export const ServiceAccordion: React.FC<ServiceAccordionProps> = ({
  service,
  isExpanded,
  onExpandedChange,
  onServiceToggle,
  onWorkflowNotesChange,
  onWorkflowToggle,
  disabled = false,
  notConfigured = false,
}) => {
  const enabledCount = service.workflows.filter((w) => w.is_enabled).length;
  const totalCount = service.workflows.length;

  const handleServiceToggle = (checked: boolean) => {
    onServiceToggle(checked);
    // Auto-expand when turning on
    if (checked && !isExpanded) {
      onExpandedChange(true);
    }
  };

  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={onExpandedChange}
      className={cn(
        'border rounded-lg transition-colors',
        getServiceBorderClass(service.is_active, notConfigured),
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2">
        <CollapsibleTrigger
          className="flex items-center gap-2 flex-1 min-w-0 text-left group"
          disabled={disabled}
        >
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 text-zinc-500 transition-transform duration-200 flex-shrink-0',
              isExpanded && 'rotate-180',
            )}
          />
          <span className="text-xs font-medium text-zinc-950 dark:text-white truncate">
            {service.display_name}
          </span>
          {notConfigured && (
            <Badge color="amber" className="text-[10px] py-0 px-1.5 ml-1">
              Not configured
            </Badge>
          )}
        </CollapsibleTrigger>

        <div className="flex items-center gap-3 flex-shrink-0">
          {totalCount > 0 && !notConfigured && (
            <Badge color="zinc" className="text-xs">
              {enabledCount}/{totalCount}
            </Badge>
          )}
          <Switch
            checked={service.is_active}
            onChange={handleServiceToggle}
            disabled={disabled || notConfigured}
            color="blue"
          />
        </div>
      </div>

      {/* Content: Workflow list */}
      <CollapsibleContent>
        <div className="border-t border-zinc-100 dark:border-zinc-800">
          <ServiceContent
            service={service}
            notConfigured={notConfigured}
            disabled={disabled}
            onWorkflowToggle={onWorkflowToggle}
            onWorkflowNotesChange={onWorkflowNotesChange}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
